import type { User } from 'firebase/auth';
import { useEffect } from 'react';
import { useSettings } from '@/hooks/useSettings';
import { locationService, userService } from '@/services';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
const HEARTBEAT_THROTTLE_MS = 5 * 60 * 1000;

let lastSentAt = 0;

export async function sendActivityHeartbeat(user: User | null, locationAllowed: boolean) {
  if (!user) return;
  console.log('Sending activity heartbeat');
  lastSentAt = Date.now();
  try {
    const updates: Parameters<typeof userService.updateProfile>[1] = {
      lastActiveAt: new Date().toISOString(),
    };

    if (locationAllowed && locationService.isSupported()) {
      const coords = await locationService.getCurrentLocation({ maximumAge: 0 });
      if (coords) {
        updates.latitude = coords.latitude;
        updates.longitude = coords.longitude;
      }
    }

    await userService.updateProfile(user.uid, updates);
  } catch (err) {
    console.error('Failed to update activity heartbeat:', err);
  }
}

export function useActivityHeartbeat(user: User | null) {
  const { settings, isLoading } = useSettings(user);
  const locationAllowed = settings?.locationAllowed ?? false;

  useEffect(() => {
    if (!user || isLoading) return undefined;

    const send = async () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastSentAt < HEARTBEAT_THROTTLE_MS) return;
      lastSentAt = now;
      await sendActivityHeartbeat(user, locationAllowed);
    };

    void send();

    const intervalId = setInterval(send, HEARTBEAT_INTERVAL_MS);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void send();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, locationAllowed, isLoading]);
}
